import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import TextToSpeechPlayer from './TextToSpeechPlayer';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import accessibilityPanelData from '../data/components/accessibility-panel.json';
import * as S from '../styles/components/AccessibilityPanel.styles';

interface AccessibilitySettings {
  visualImpairment: boolean;
  increaseFontSize: number; // 0 = normal, 1-5 = levels
  increaseLineHeight: number; // 0 = normal, 1-5 = levels
  textToSpeech: boolean;
}

const defaultSettings: AccessibilitySettings = {
  visualImpairment: false,
  increaseFontSize: 0,
  increaseLineHeight: 0,
  textToSpeech: false,
};

interface AccessibilityPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccessibilityPanel: React.FC<AccessibilityPanelProps> = ({ isOpen, onClose }) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [showTextToSpeech, setShowTextToSpeech] = useState(false);
  const panelContentRef = useRef<HTMLDivElement>(null);
  const panelContainerRef = useRef<HTMLDivElement>(null);
  
  // Read language from localStorage (managed by Layout)
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  // Listen for language changes from Layout
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent | StorageEvent) => {
      const newLang = e instanceof CustomEvent ? e.detail : localStorage.getItem('siteLanguage');
      if (newLang === 'en' || newLang === 'de') {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    window.addEventListener('storage', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleLanguageChange as EventListener);
    };
  }, []);

  // Load settings from localStorage on mount
  useEffect(() => {
    // Prüfe ob funktionale Cookies erlaubt sind
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      const savedPrefs = localStorage.getItem('cookiePreferences');
      if (savedPrefs) {
        try {
          const prefs = JSON.parse(savedPrefs);
          if (!prefs.functional) {
            // Funktionale Cookies nicht erlaubt - nicht aus localStorage laden
            return;
          }
        } catch (e) {
          // Fehler beim Parsen - Standard-Verhalten
        }
      }
    }
    const saved = localStorage.getItem('accessibilitySettings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse accessibility settings:', e);
      }
    }
  }, []);

  // Apply settings to document
  useEffect(() => {
    applySettings(settings);
  }, [settings]);

  // Get all focusable elements within the panel
  const getFocusableElements = React.useCallback(() => {
    if (!panelContainerRef.current) return [];
    
    const focusableSelectors = [
      'input:not([disabled]):not([type="hidden"])',
      'button:not([disabled])',
      'a[href]',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    return Array.from(
      panelContainerRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
    ).filter(el => {
      // Filter out elements that are not visible
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    });
  }, []);

  // Helper function to close panel and stop text-to-speech
  const closePanelAndStopSpeech = React.useCallback(() => {
    if (showTextToSpeech) {
      window.speechSynthesis.cancel();
      setShowTextToSpeech(false);
    }
    onClose();
  }, [showTextToSpeech, onClose]);

  // ESC-Taste zum Schließen + Focus Trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || !panelContainerRef.current) return;

      // Wenn Text-to-Speech Player aktiv ist, nicht eingreifen (Player hat Priorität)
      if (showTextToSpeech && e.key === 'Tab') {
        const playerContainer = document.querySelector('[data-text-to-speech-player]');
        const activeElement = document.activeElement as HTMLElement;
        // Wenn Player aktiv ist, lasse den Player-Focus Trap übernehmen
        if (playerContainer) {
          if (playerContainer.contains(activeElement) || showTextToSpeech) {
            // Player-Focus Trap übernimmt - nicht eingreifen
            return;
          }
        }
      }

      // Focus Trap: Keep Tab navigation within the panel (nur wenn Player nicht aktiv)
      if (e.key === 'Tab' && !showTextToSpeech) {
        const focusableElements = getFocusableElements();
        
        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement as HTMLElement;

        // Check if active element is within the panel
        const isWithinPanel = panelContainerRef.current.contains(activeElement);

        // If focus is outside panel, focus first element
        if (!isWithinPanel) {
          e.preventDefault();
          firstElement.focus();
          return;
        }

        // Handle Tab navigation within panel
        if (e.shiftKey) {
          // Shift+Tab: If on first element, wrap to last
          if (activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: If on last element, wrap to first
          if (activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
        return;
      }

      // ESC-Taste zum Schließen
      if (e.key === 'Escape') {
        e.preventDefault();
        // Remove focus before closing
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        // Stop text-to-speech if active
        if (showTextToSpeech) {
          window.speechSynthesis.cancel();
          setShowTextToSpeech(false);
        }
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose, showTextToSpeech, getFocusableElements]);

  const applySettings = (newSettings: AccessibilitySettings) => {
    const root = document.documentElement;
    
    // Seheinschränkungen: Erhöhter Kontrast
    if (newSettings.visualImpairment) {
      root.style.setProperty('--accessibility-contrast', '1.3');
      root.style.setProperty('--accessibility-brightness', '1.1');
      root.classList.add('high-contrast-mode');
    } else {
      root.style.setProperty('--accessibility-contrast', '1');
      root.style.setProperty('--accessibility-brightness', '1');
      root.classList.remove('high-contrast-mode');
    }
    
    // Schriftgröße erhöhen
    const fontSizeMultiplier = 1 + (newSettings.increaseFontSize * 0.1); // +10% per level
    root.style.setProperty('--accessibility-font-size', fontSizeMultiplier.toString());
    
    if (newSettings.increaseFontSize > 0) {
      root.classList.add('accessibility-font-active');
      console.log(`✅ Schriftgröße: +${newSettings.increaseFontSize * 10}%`);
    } else {
      root.classList.remove('accessibility-font-active');
      console.log('✅ Schriftgröße: Normal');
    }
    
    // Zeilenabstand erhöhen
    const lineHeightMultiplier = 1 + (newSettings.increaseLineHeight * 0.15); // +15% per level
    root.style.setProperty('--accessibility-line-height', lineHeightMultiplier.toString());
    
    if (newSettings.increaseLineHeight > 0) {
      root.classList.add('accessibility-lineheight-active');
      console.log(`✅ Zeilenabstand: +${newSettings.increaseLineHeight * 15}%`);
    } else {
      root.classList.remove('accessibility-lineheight-active');
      console.log('✅ Zeilenabstand: Normal');
    }
    
    // Text-to-Speech - Player wird separat gerendert
    if (newSettings.textToSpeech) {
      root.classList.add('text-to-speech-active');
      console.log('✅ Text-to-Speech Player aktiviert');
    } else {
      root.classList.remove('text-to-speech-active');
      console.log('✅ Text-to-Speech Player deaktiviert');
    }
    
  };

  const updateSetting = (key: keyof AccessibilitySettings, value: boolean | number) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    // Nur in localStorage speichern, wenn funktionale Cookies erlaubt sind
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      const savedPrefs = localStorage.getItem('cookiePreferences');
      if (savedPrefs) {
        try {
          const prefs = JSON.parse(savedPrefs);
          if (prefs.functional) {
            localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
          }
          // Wenn nicht erlaubt: Einstellungen werden trotzdem angewendet, aber nicht gespeichert
        } catch (e) {
          // Bei Fehler: speichern (Standard-Verhalten)
          localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
        }
      } else {
        // Keine Präferenzen: speichern (Standard-Verhalten)
        localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
      }
    } else {
      // Keine Cookie-Zustimmung: speichern (Standard-Verhalten bis User entscheidet)
      localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
    }
  };
  
  const resetSettings = () => {
    setSettings(defaultSettings);
    // Nur in localStorage speichern, wenn funktionale Cookies erlaubt sind
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      const savedPrefs = localStorage.getItem('cookiePreferences');
      if (savedPrefs) {
        try {
          const prefs = JSON.parse(savedPrefs);
          if (prefs.functional) {
            localStorage.setItem('accessibilitySettings', JSON.stringify(defaultSettings));
          }
        } catch (e) {
          localStorage.setItem('accessibilitySettings', JSON.stringify(defaultSettings));
        }
      } else {
        localStorage.setItem('accessibilitySettings', JSON.stringify(defaultSettings));
      }
    } else {
      localStorage.setItem('accessibilitySettings', JSON.stringify(defaultSettings));
    }
  };

  // Stop text-to-speech when panel closes
  useEffect(() => {
    if (!isOpen) {
      if (showTextToSpeech) {
        window.speechSynthesis.cancel();
        setShowTextToSpeech(false);
      }
    }
  }, [isOpen, showTextToSpeech]);

  if (!isOpen && !settings.textToSpeech) return null;

  return (
    <>
    <AnimatePresence>
      {isOpen && (
      <S.Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <S.PanelContainer
          ref={panelContainerRef}
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <S.PanelHeader colors={colors}>
            <S.HeaderTitle>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="7" r="1.5" fill="currentColor"/>
                <line x1="12" y1="8.5" x2="12" y2="14"/>
                <line x1="7" y1="11" x2="17" y2="11"/>
                <line x1="10" y1="18" x2="12" y2="14"/>
                <line x1="14" y1="18" x2="12" y2="14"/>
              </svg>
              {accessibilityPanelData[language].title}
            </S.HeaderTitle>
            <S.CloseButton onClick={closePanelAndStopSpeech} aria-label={accessibilityPanelData[language].closeButton}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </S.CloseButton>
          </S.PanelHeader>

          <S.PanelContent ref={panelContentRef}>
            <div style={{ marginBottom: '24px', padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 'clamp(0.9rem, 2vw, 0.85rem)', color: '#6b7280', flex: 1, minWidth: '200px' }}>
                {accessibilityPanelData[language].readAloudDescription}
              </div>
              <button
                onClick={() => {
                  setShowTextToSpeech(true);
                }}
                style={{
                  fontSize: 'clamp(1rem, 2.5vw, 0.9rem)',
                  padding: '0.6rem 1.2rem',
                  flexShrink: 0,
                  background: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = colors.primary;
                }}
              >
                {accessibilityPanelData[language].readAloud}
              </button>
            </div>
            <S.SettingGroup>
              <S.SettingLabel>{accessibilityPanelData[language].settings.visualImpairment.label}</S.SettingLabel>
              <S.SettingDescription>
                {accessibilityPanelData[language].settings.visualImpairment.description}
              </S.SettingDescription>
              <S.ToggleContainer>
                <S.ToggleButtonGroup>
                  <S.ToggleButton
                    colors={colors}
                    isActive={!settings.visualImpairment}
                    onClick={() => updateSetting('visualImpairment', false)}
                  >
                    {accessibilityPanelData[language].toggle.off}
                  </S.ToggleButton>
                  <S.ToggleButton
                    colors={colors}
                    isActive={settings.visualImpairment}
                    onClick={() => updateSetting('visualImpairment', true)}
                  >
                    {accessibilityPanelData[language].toggle.on}
                  </S.ToggleButton>
                </S.ToggleButtonGroup>
              </S.ToggleContainer>
            </S.SettingGroup>

            <S.SettingGroup>
              <S.SettingLabel>{accessibilityPanelData[language].settings.textToSpeech.label}</S.SettingLabel>
              <S.SettingDescription>
                {accessibilityPanelData[language].settings.textToSpeech.description}
              </S.SettingDescription>
              <S.ToggleContainer>
                <S.ToggleButtonGroup>
                  <S.ToggleButton
                    colors={colors}
                    isActive={!settings.textToSpeech}
                    onClick={() => updateSetting('textToSpeech', false)}
                  >
                    {accessibilityPanelData[language].toggle.off}
                  </S.ToggleButton>
                  <S.ToggleButton
                    colors={colors}
                    isActive={settings.textToSpeech}
                    onClick={() => {
                      updateSetting('textToSpeech', true);
                      onClose();
                    }}
                  >
                    {accessibilityPanelData[language].toggle.on}
                  </S.ToggleButton>
                </S.ToggleButtonGroup>
              </S.ToggleContainer>
            </S.SettingGroup>

            <S.SettingGroup>
              <S.SettingLabel>{accessibilityPanelData[language].settings.increaseFontSize.label}</S.SettingLabel>
              <S.SettingDescription>
                {accessibilityPanelData[language].settings.increaseFontSize.description}
              </S.SettingDescription>
              <S.SliderContainer>
                <S.SliderButton
                  colors={colors}
                  onClick={() => updateSetting('increaseFontSize', Math.max(0, settings.increaseFontSize - 1))}
                  disabled={settings.increaseFontSize === 0}
                  aria-label={`${accessibilityPanelData[language].settings.increaseFontSize.label} ${accessibilityPanelData[language].slider.decrease}`}
                >
                  −
                </S.SliderButton>
                <S.SliderDisplay>
                  {settings.increaseFontSize === 0 ? accessibilityPanelData[language].slider.normal : `+${settings.increaseFontSize * 10}%`}
                </S.SliderDisplay>
                <S.SliderButton
                  colors={colors}
                  onClick={() => updateSetting('increaseFontSize', Math.min(5, settings.increaseFontSize + 1))}
                  disabled={settings.increaseFontSize === 5}
                  aria-label={`${accessibilityPanelData[language].settings.increaseFontSize.label} ${accessibilityPanelData[language].slider.increase}`}
                >
                  +
                </S.SliderButton>
                {settings.increaseFontSize > 0 && (
                  <S.ResetButton colors={colors} onClick={() => updateSetting('increaseFontSize', 0)}>
                    {accessibilityPanelData[language].slider.reset}
                  </S.ResetButton>
                )}
              </S.SliderContainer>
            </S.SettingGroup>

            <S.SettingGroup>
              <S.SettingLabel>{accessibilityPanelData[language].settings.increaseLineHeight.label}</S.SettingLabel>
              <S.SettingDescription>
                {accessibilityPanelData[language].settings.increaseLineHeight.description}
              </S.SettingDescription>
              <S.SliderContainer>
                <S.SliderButton
                  colors={colors}
                  onClick={() => updateSetting('increaseLineHeight', Math.max(0, settings.increaseLineHeight - 1))}
                  disabled={settings.increaseLineHeight === 0}
                  aria-label={`${accessibilityPanelData[language].settings.increaseLineHeight.label} ${accessibilityPanelData[language].slider.decrease}`}
                >
                  −
                </S.SliderButton>
                <S.SliderDisplay>
                  {settings.increaseLineHeight === 0 ? accessibilityPanelData[language].slider.normal : `+${settings.increaseLineHeight * 15}%`}
                </S.SliderDisplay>
                <S.SliderButton
                  colors={colors}
                  onClick={() => updateSetting('increaseLineHeight', Math.min(5, settings.increaseLineHeight + 1))}
                  disabled={settings.increaseLineHeight === 5}
                  aria-label={`${accessibilityPanelData[language].settings.increaseLineHeight.label} ${accessibilityPanelData[language].slider.increase}`}
                >
                  +
                </S.SliderButton>
                {settings.increaseLineHeight > 0 && (
                  <S.ResetButton colors={colors} onClick={() => updateSetting('increaseLineHeight', 0)}>
                    {accessibilityPanelData[language].slider.reset}
                  </S.ResetButton>
                )}
              </S.SliderContainer>
            </S.SettingGroup>

            <S.SettingGroup style={{ borderBottom: 'none' }}>
              <S.SettingLabel>
                {accessibilityPanelData[language].keyboardInfo.title}
              </S.SettingLabel>
              <S.SettingDescription>
                {accessibilityPanelData[language].keyboardInfo.description}
              </S.SettingDescription>
            </S.SettingGroup>

            <S.SettingGroup style={{ borderBottom: 'none' }}>
              <S.ToggleContainer style={{ justifyContent: 'center' }}>
                <S.ResetButton 
                  colors={colors}
                  onClick={resetSettings}
                  style={{ 
                    padding: '12px 32px',
                    fontSize: '1rem',
                    borderColor: '#991b1b',
                    color: '#991b1b'
                  }}
                >
                  {accessibilityPanelData[language].resetAll}
                </S.ResetButton>
              </S.ToggleContainer>
            </S.SettingGroup>
          </S.PanelContent>
        </S.PanelContainer>
      </S.Overlay>
      )}
    </AnimatePresence>
    
    {/* Text-to-Speech Player für Barrierefreiheitseinstellungen */}
    {isOpen && (
      <TextToSpeechPlayer 
        isActive={showTextToSpeech} 
        onClose={() => setShowTextToSpeech(false)}
        targetElement={panelContainerRef.current}
      />
    )}
    
    {/* Text-to-Speech Player für allgemeine Text-to-Speech Funktion */}
    <TextToSpeechPlayer 
      isActive={settings.textToSpeech} 
      onClose={() => updateSetting('textToSpeech', false)} 
    />
    </>
  );
};

export default AccessibilityPanel;

